<template>
  <div class="px-4 transactions-section">
    <h4 class="fw-bold mb-3">Transactions</h4>

    <!-- Search -->
    <div class="search-wrapper">
      <input
        type="text"
        class="form-control custom-search-input"
        placeholder="🔍 Search requests"
        v-model="searchQuery"
        @input="debounceSearch"
      />
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- No transactions message -->
    <div v-else-if="transactions.length === 0" class="text-center py-4">
      <p class="mb-0">No transactions found.</p>
    </div>

    <!-- Transactions table -->
    <table v-else class="table table-hover">
      <thead class="table-light">
        <tr>
          <th>Request No.</th>
          <th>Email Address</th>
          <th>Amount</th>
          <th>Binance Id</th>
          <th>Date</th>
          <th>Time</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(txn, index) in transactions" :key="index">
          <td>{{ txn.requestNo }}</td>
          <td>{{ txn.email }}</td>
          <td>{{ txn.amount }} <small>BTC</small></td>
          <td>{{ txn.binanceId }}</td>
          <td>{{ txn.date }}</td>
          <td>{{ txn.time }}</td>
          <td>
            <span :class="txn.statusClass">{{ txn.status }}</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div
      class="mt-3 d-flex justify-content-between align-items-center"
      v-if="transactions.length > 0"
    >
      <div>
        <span class="text-muted">
          Showing {{ getStartItem() }} to {{ getEndItem() }} of
          {{ meta.total }} entries
        </span>
      </div>
      <nav v-if="meta.last_page > 1">
        <ul class="pagination mb-0">
          <li class="page-item" :class="{ disabled: meta.current_page === 1 }">
            <a
              class="page-link"
              href="#"
              @click.prevent="changePage(meta.current_page - 1)"
            >
              Previous
            </a>
          </li>

          <li
            v-for="page in getPageNumbers()"
            :key="page"
            class="page-item"
            :class="{ active: page === meta.current_page }"
          >
            <a class="page-link" href="#" @click.prevent="changePage(page)">{{
              page
            }}</a>
          </li>

          <li
            class="page-item"
            :class="{ disabled: meta.current_page === meta.last_page }"
          >
            <a
              class="page-link"
              href="#"
              @click.prevent="changePage(meta.current_page + 1)"
            >
              Next
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      transactions: [],
      searchQuery: "",
      loading: false,
      perPage: 10,
      meta: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
      },
      searchTimeout: null,
    };
  },
  methods: {
    async fetchTransactions(page = 1) {
      this.loading = true;

      try {
        const res = await this.$store.dispatch("user/getWalletTransactions", {
          page,
          perPage: this.perPage,
          search: this.searchQuery.trim(),
        });

        if (res && res.status) {
          this.transactions = (res.data || []).map((txn) => ({
            requestNo: txn.request_no || txn.id || "N/A",
            email: txn.email,
            amount:
              typeof txn.amount === "number"
                ? txn.amount.toFixed(6)
                : txn.amount,
            binanceId: txn.wallet_address,
            date: this.formatDate(txn.requested_at),
            time: this.formatTime(txn.requested_at),
            status: txn.status,
            statusClass: this.getStatusClass(txn.status),
          }));

          this.meta = res.meta || {
            current_page: 1,
            last_page: 1,
            total: this.transactions.length,
            per_page: this.perPage,
          };
        } else {
          this.transactions = [];
          console.error("Failed to load transactions:", res?.message);
        }
      } catch (e) {
        console.error("Fetch error:", e);
        this.transactions = [];
      } finally {
        this.loading = false;
      }
    },

    debounceSearch() {
      // Clear any existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set a new timeout to delay the search
      this.searchTimeout = setTimeout(() => {
        this.meta.current_page = 1; // Reset to first page on search
        this.fetchTransactions(1);
      }, 500); // 500ms debounce
    },

    changePage(page) {
      if (page >= 1 && page <= this.meta.last_page) {
        this.meta.current_page = page;
        this.fetchTransactions(page);
      }
    },

    getStatusClass(status) {
      switch (status) {
        case "approved":
          return "badge bg-success";
        case "rejected":
          return "badge bg-danger";
        default:
          return "badge bg-warning text-dark";
      }
    },

    formatDate(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toLocaleDateString();
    },

    formatTime(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getStartItem() {
      return (this.meta.current_page - 1) * this.meta.per_page + 1;
    },

    getEndItem() {
      return Math.min(
        this.meta.current_page * this.meta.per_page,
        this.meta.total
      );
    },

    getPageNumbers() {
      const totalPages = this.meta.last_page;
      const currentPage = this.meta.current_page;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + 4);

      if (endPage === totalPages) {
        startPage = Math.max(1, endPage - 4);
      }

      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    },
  },
  mounted() {
    this.fetchTransactions();
  },
};
</script>

<style scoped>
.transactions-section {
  background-color: #fff;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
}

.pagination {
  margin-bottom: 0;
}

/* .badge {
  font-size: 85%;
  padding: 0.35em 0.65em;
} */

.badge {
  display: inline-block;
  padding: 0.4em 1em;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 999px; /* pill shape */
  text-align: center;
  white-space: nowrap;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: 0.3s ease;
}

.bg-success {
  background: linear-gradient(to right, #4caf50, #81c784);
  color: white;
}

.bg-danger {
  background: linear-gradient(to right, #e53935, #ef5350);
  color: white;
}

.bg-warning {
  background: linear-gradient(to right, #fdd835, #ffb300);
  color: #333;
}

.search-wrapper {
  max-width: 400px;
  margin-bottom: 1rem;
}

.custom-search-input {
  width: 100%;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  border: 1px solid #ccc;
  box-shadow: 0 0 0 3px rgba(100, 149, 237, 0.1);
  transition: box-shadow 0.3s ease;
}

.custom-search-input:focus {
  border-color: #2684ff;
  box-shadow: 0 0 0 3px rgba(38, 132, 255, 0.3);
}
</style>
